import { ConfirmModal } from '@src/components/common/ConfirmModal';
import { LayoutTemplateRegistry } from '@src/editor/models/layout/LayoutTemplateRegistry';
import { TemplateIds } from '@src/editor/models/layout/presetTemplates';
import { useRefreshActiveElement } from '@src/hooks/useRefreshActiveElement';
import useUpdateExistingScene from '@src/hooks/useUpdateExistingScene';
import { useEditor, useFeatureFlags } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { ActionButton } from '@src/styles/mainStyle';
import ContainIcon from '@src/svgs/fit/ContainIcon';
import CoverIcon from '@src/svgs/fit/CoverIcon';
import FillIcon from '@src/svgs/fit/FillIcon';
import SplitPresentationIcon from '@src/svgs/fit/SplitPresentation';
import SplitTwoIcon from '@src/svgs/fit/SplitTwoIcon';
import SplitTwoVerticalIcon from '@src/svgs/fit/SplitTwoVerticalIcon';
import { LogManager } from '@src/utility/Logger';
import {
  type ElementTiming,
  resolveSceneRange,
  type SceneRange,
} from '@src/utility/sceneAnchors';
import { observer } from 'mobx-react-lite';
import {
  type FC,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { PADDING_LEFT_COMPENSATION } from './TimelineElement';

import type { Scene } from '@src/editor/models/layout/Scene';

const getIconColor = (isSelected: boolean) =>
  isSelected ? 'var(--primary-hover)' : 'var(--text-secondary)';

const getTemplateIcon = (templateId: string, isSelected: boolean) => {
  const color = getIconColor(isSelected);
  switch (templateId) {
    case TemplateIds.Cover:
      return <CoverIcon strokeColor={color} />;
    case TemplateIds.Contain:
    case TemplateIds.BlurredBackground:
      return <ContainIcon strokeColor={color} />;
    case TemplateIds.Fill:
      return <FillIcon strokeColor={color} />;
    case TemplateIds.SplitVertical:
      return <SplitTwoVerticalIcon strokeColor={color} />;
    case TemplateIds.SplitHorizontal:
      return <SplitTwoIcon strokeColor={color} />;
    case TemplateIds.Stacked1To3:
      return <SplitPresentationIcon strokeColor={color} />;
    default:
      return <CoverIcon strokeColor={color} />;
  }
};

interface TimelineSceneElementProps {
  scene: Scene;
  elementTimingMap: Map<string, ElementTiming>;
  previewRanges?: Record<string, SceneRange>;
  isViewOnly?: boolean;
  onSplitSceneAtPlayhead?: () => boolean;
}

const MIN_PIXEL_WIDTH = 6;
const MIN_SCENE_LABEL_WIDTH = 45;
const CONTEXT_MENU_WIDTH = 160;
const CONTEXT_MENU_HEIGHT = 144;
const LAYOUT_DROPDOWN_WIDTH = 120;
const LAYOUT_DROPDOWN_HEIGHT = 160;
export const logger = LogManager.getLogger('component:TimelineSceneElement');

export const TimelineSceneElement: FC<TimelineSceneElementProps> = observer(
  ({
    scene,
    elementTimingMap,
    previewRanges = {},
    isViewOnly = false,
    onSplitSceneAtPlayhead,
  }) => {
    const videoCreator = useVideoCreatorStore();
    const editorStore = useEditor();
    const flags = useFeatureFlags();
    const {
      enableStackedLayouts,
      creatorStudioTimelineEnableFit13,
      creatorStudioTimelineEnableFitFill,
      creatorStudioTimelineEnableFitSplitHorizontal11,
      creatorStudioTimelineEnableFitSplitHorizontal13,
      creatorStudioTimelineEnableFitSplitVertical11,
      creatorStudioTimelineEnableFitZoomCall,
    } = flags;
    const timelineScale = videoCreator.timelineScale;
    const durationSeconds = Number(videoCreator.duration) || 0;

    const templateRegistry = useMemo(
      () => LayoutTemplateRegistry.getInstance(),
      [],
    );
    const [contextMenuPosition, setContextMenuPosition] = useState<{
      x: number;
      y: number;
    } | null>(null);
    const contextMenuRef = useRef<HTMLDivElement | null>(null);
    const [layoutDropdownPosition, setLayoutDropdownPosition] = useState<{
      x: number;
      y: number;
    } | null>(null);
    const layoutDropdownRef = useRef<HTMLDivElement | null>(null);
    const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);

    const templateOptions = useMemo(() => {
      return templateRegistry
        .getAllTemplates()
        .filter(template => {
          if (template.id === TemplateIds.Fill) {
            return creatorStudioTimelineEnableFitFill;
          }
          if (template.id === TemplateIds.SplitHorizontal) {
            return (
              enableStackedLayouts &&
              creatorStudioTimelineEnableFitSplitHorizontal11
            );
          }
          if (template.id === TemplateIds.Stacked1To3) {
            return (
              enableStackedLayouts &&
              creatorStudioTimelineEnableFit13 &&
              creatorStudioTimelineEnableFitSplitHorizontal13
            );
          }
          if (template.id === TemplateIds.SplitVertical) {
            return creatorStudioTimelineEnableFitSplitVertical11;
          }
          if (template.id === TemplateIds.SplitHorizontal) {
            return (
              enableStackedLayouts &&
              creatorStudioTimelineEnableFitSplitHorizontal11
            );
          }
          if (template.id === TemplateIds.Stacked1To3) {
            return (
              enableStackedLayouts &&
              creatorStudioTimelineEnableFit13 &&
              creatorStudioTimelineEnableFitSplitHorizontal13
            );
          }
          if (template.id === TemplateIds.ZoomCall) {
            return creatorStudioTimelineEnableFitZoomCall;
          }
          return true;
        })
        .map(template => ({
          id: template.id,
          label: template.name || template.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }, [
      templateRegistry,
      enableStackedLayouts,
      creatorStudioTimelineEnableFit13,
      creatorStudioTimelineEnableFitFill,
      creatorStudioTimelineEnableFitSplitHorizontal11,
      creatorStudioTimelineEnableFitSplitHorizontal13,
      creatorStudioTimelineEnableFitSplitVertical11,
    ]);

    const baseRange = useMemo(
      () => resolveSceneRange(scene, elementTimingMap, durationSeconds),
      [scene, elementTimingMap, durationSeconds],
    );

    const effectiveRange = previewRanges[scene.id] ?? baseRange;

    const duration = Math.max(effectiveRange.end - effectiveRange.start, 0);
    const width = Math.max(duration * timelineScale, MIN_PIXEL_WIDTH);
    const isSceneLabelVisible = width >= MIN_SCENE_LABEL_WIDTH;
    const left =
      PADDING_LEFT_COMPENSATION + effectiveRange.start * timelineScale;

    const currentTemplate = templateRegistry.getTemplate(
      scene.layout.layoutTemplateId,
    );
    const label = currentTemplate?.name || scene.layout.layoutTemplateId; // fallback to id for debugging

    const refreshActiveElement = useRefreshActiveElement();
    const { updateExistingScene } = useUpdateExistingScene();

    const handleTemplateSelect = useCallback(
      async (nextTemplateId: string) => {
        setLayoutDropdownPosition(null);

        if (nextTemplateId === scene.layout.layoutTemplateId) {
          return;
        }

        // important to capture the base element before changing the layout
        const baseElement =
          videoCreator.getRealActiveElement?.() ??
          videoCreator.getActiveElement?.();

        const success = await updateExistingScene(
          scene,
          nextTemplateId,
          undefined, // element source will be picked based on a scene
        );

        if (!success) {
          logger.warn(
            `Failed to update scene ${scene.id} with template ${nextTemplateId}`,
          );
          return;
        }

        await refreshActiveElement(baseElement);
      },
      [refreshActiveElement, scene, updateExistingScene, videoCreator],
    );

    const openLayoutDropdown = useCallback(
      (event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const maxX = Math.max(0, viewportWidth - LAYOUT_DROPDOWN_WIDTH);
        const maxY = Math.max(0, viewportHeight - LAYOUT_DROPDOWN_HEIGHT);

        const clampedX = Math.min(Math.max(0, event.clientX), maxX);
        const clampedY = Math.min(Math.max(0, event.clientY), maxY);

        setLayoutDropdownPosition({ x: clampedX, y: clampedY });
      },
      [],
    );

    const closeLayoutDropdown = useCallback(() => {
      setLayoutDropdownPosition(null);
    }, []);

    const closeContextMenu = useCallback(() => {
      setContextMenuPosition(null);
    }, []);

    const handleSceneContextMenu = useCallback(
      (event: MouseEvent<HTMLDivElement>) => {
        event.preventDefault();

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const maxX = Math.max(0, viewportWidth - CONTEXT_MENU_WIDTH);
        const maxY = Math.max(0, viewportHeight - CONTEXT_MENU_HEIGHT);

        const clampedX = Math.min(Math.max(0, event.clientX), maxX);
        const clampedY = Math.min(Math.max(0, event.clientY), maxY);

        setContextMenuPosition({ x: clampedX, y: clampedY });
      },
      [],
    );

    useEffect(() => {
      if (!contextMenuPosition) {
        return;
      }

      const handlePointerDown = (event: globalThis.MouseEvent) => {
        const target = event.target;
        if (
          target instanceof Node &&
          contextMenuRef.current?.contains(target)
        ) {
          return;
        }

        closeContextMenu();
      };

      const handleKeyDown = (event: globalThis.KeyboardEvent) => {
        if (event.key === 'Escape') {
          closeContextMenu();
        }
      };

      const handleBlur = () => {
        closeContextMenu();
      };

      window.addEventListener('pointerdown', handlePointerDown);
      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('blur', handleBlur);

      return () => {
        window.removeEventListener('pointerdown', handlePointerDown);
        window.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('blur', handleBlur);
      };
    }, [contextMenuPosition, closeContextMenu]);

    useEffect(() => {
      if (!contextMenuPosition) {
        return;
      }

      const firstButton = contextMenuRef.current?.querySelector('button');
      if (firstButton instanceof HTMLButtonElement) {
        firstButton.focus();
      }
    }, [contextMenuPosition]);

    useEffect(() => {
      if (!layoutDropdownPosition) {
        return;
      }

      const handlePointerDown = (event: globalThis.MouseEvent) => {
        const target = event.target;
        if (
          target instanceof Node &&
          layoutDropdownRef.current?.contains(target)
        ) {
          return;
        }

        closeLayoutDropdown();
      };

      const handleKeyDown = (event: globalThis.KeyboardEvent) => {
        if (event.key === 'Escape') {
          closeLayoutDropdown();
        }
      };

      const handleBlur = () => {
        closeLayoutDropdown();
      };

      window.addEventListener('pointerdown', handlePointerDown);
      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('blur', handleBlur);

      return () => {
        window.removeEventListener('pointerdown', handlePointerDown);
        window.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('blur', handleBlur);
      };
    }, [layoutDropdownPosition, closeLayoutDropdown]);

    const handleDeleteScene = useCallback(() => {
      closeContextMenu();
      const success = editorStore.editor.commandRemoveScene(scene.id);
      if (!success) {
        logger.warn(`Failed to delete scene ${scene.id}`);
      }
    }, [closeContextMenu, editorStore.editor, scene.id]);

    const handleDeleteAllScenesClick = useCallback(() => {
      // Close the menu and surface the standard confirm modal before any
      // destructive action — deleting every scene must be confirmed.
      closeContextMenu();
      setShowDeleteAllConfirm(true);
    }, [closeContextMenu]);

    const handleConfirmDeleteAllScenes = useCallback(() => {
      setShowDeleteAllConfirm(false);
      // Capture ids up front: commandRemoveScene mutates the scenes array as it
      // removes, so iterating it live would skip entries. Each removal is its
      // own command, so undo reverts one scene at a time.
      const sceneIds = editorStore.editor.timeline.scenes.map(s => s.id);
      for (const id of sceneIds) {
        const success = editorStore.editor.commandRemoveScene(id);
        if (!success) {
          logger.warn(`Failed to delete scene ${id}`);
        }
      }
    }, [editorStore.editor]);

    const handleSplitScene = useCallback(() => {
      closeContextMenu();
      onSplitSceneAtPlayhead?.();
    }, [closeContextMenu, onSplitSceneAtPlayhead]);

    return (
      <>
        <SceneBar
          padded={isSceneLabelVisible}
          style={{ left, width }}
          onClick={isViewOnly ? undefined : openLayoutDropdown}
          onContextMenu={isViewOnly ? undefined : handleSceneContextMenu}
          isViewOnly={isViewOnly}
        >
          {isSceneLabelVisible && (
            <SceneLabel isViewOnly={isViewOnly}>
              <SceneName title={label}>{label}</SceneName>
            </SceneLabel>
          )}
        </SceneBar>
        {layoutDropdownPosition &&
          typeof document !== 'undefined' &&
          createPortal(
            <LayoutDropdownContainer
              ref={layoutDropdownRef}
              role="listbox"
              aria-label="Scene layout template"
              style={{
                top: layoutDropdownPosition.y,
                left: layoutDropdownPosition.x,
              }}
            >
              {templateOptions.map(option => {
                const isSelected = option.id === scene.layout.layoutTemplateId;
                return (
                  <LayoutDropdownItem
                    key={option.id}
                    role="option"
                    aria-selected={isSelected}
                    isSelected={isSelected}
                    onClick={() => handleTemplateSelect(option.id)}
                  >
                    <LayoutItemIcon>
                      {getTemplateIcon(option.id, isSelected)}
                    </LayoutItemIcon>
                    {option.label}
                  </LayoutDropdownItem>
                );
              })}
            </LayoutDropdownContainer>,
            document.body,
          )}
        {contextMenuPosition &&
          typeof document !== 'undefined' &&
          createPortal(
            <ContextMenuContainer
              ref={contextMenuRef}
              role="menu"
              aria-label="Scene options"
              style={{
                top: contextMenuPosition.y,
                left: contextMenuPosition.x,
              }}
            >
              <ContextMenuButton
                role="menuitem"
                onClick={handleSplitScene}
                aria-label="Split scene"
              >
                Split Scene
              </ContextMenuButton>
              <ContextMenuButton
                role="menuitem"
                $danger
                onClick={handleDeleteScene}
                aria-label="Delete scene"
              >
                Delete Scene
              </ContextMenuButton>
              <ContextMenuButton
                role="menuitem"
                $danger
                onClick={handleDeleteAllScenesClick}
                aria-label="Delete all scenes"
              >
                Delete All Scenes
              </ContextMenuButton>
            </ContextMenuContainer>,
            document.body,
          )}
        {showDeleteAllConfirm && (
          <ConfirmModal
            closeModal={() => setShowDeleteAllConfirm(false)}
            onConfirm={handleConfirmDeleteAllScenes}
            onCancel={() => setShowDeleteAllConfirm(false)}
            confirmText="Delete All"
            cancelText="Cancel"
            title="Delete all scenes?"
            message="All scenes will be removed from this video."
          />
        )}
      </>
    );
  },
);

const SceneBar = styled.div<{ padded: boolean; isViewOnly?: boolean }>`
  position: absolute;
  height: 26px;
  top: 4px;
  z-index: 1;
  border-radius: var(--radius-input);
  border: 0.5px solid var(--border);
  display: flex;
  align-items: center;
  padding: ${({ padded }) => (padded ? '0 8px' : '0')};
  box-sizing: border-box;
  pointer-events: auto;
  color: var(--background);
  font-size: var(--text-xs);
  cursor: ${({ isViewOnly }) => (isViewOnly ? 'default' : 'pointer')};
  transition:
    border-color var(--transition-fast) ease,
    background-color var(--transition-fast) ease;
  &:hover {
    border-color: ${({ isViewOnly }) =>
      isViewOnly ? 'var(--border)' : 'var(--text-muted)'};
    background-color: ${({ isViewOnly }) =>
      isViewOnly ? 'transparent' : '#ffffff1a'};
  }
`;
const SceneLabel = styled.div<{ isViewOnly?: boolean }>`
  display: flex;
  align-items: center;
  color: var(--text-muted);
  gap: 4px;
  width: 100%;
  position: relative;
  transition: color var(--transition-fast) ease;

  ${SceneBar}:hover & {
    color: ${({ isViewOnly }) =>
      isViewOnly ? 'var(--text-muted)' : 'var(--foreground)'};
  }
`;

const SceneName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 0 1 auto;
  max-width: 100%;
`;

const LayoutDropdownContainer = styled.div`
  position: fixed;
  min-width: 120px;
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  box-shadow: var(--elevation-toast);
  z-index: 100000;
  color: var(--text-secondary);
  font-size: var(--text-xs);
`;

const LayoutDropdownItem = styled.div<{ isSelected: boolean }>`
  position: relative;
  display: flex;
  box-sizing: border-box;
  text-transform: capitalize;
  align-items: center;
  padding: 8px;
  gap: 8px;
  cursor: pointer;
  color: ${({ isSelected }) =>
    isSelected ? 'var(--primary-hover)' : 'var(--text-secondary)'};
  user-select: none;

  &:hover {
    background-color: var(--border);
  }

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 8px;
    width: calc(100% - 16px);
    height: 1px;
    background-color: var(--border);
  }
`;

const LayoutItemIcon = styled.div`
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ContextMenuContainer = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--spacing-xs);
  min-width: 160px;
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--elevation-toast);
  z-index: 100000;
  color: var(--foreground);
  font-size: var(--text-xs);
`;

const ContextMenuButton = styled(ActionButton).attrs({
  as: 'button',
  type: 'button',
})<{ $danger?: boolean }>`
  width: 100%;
  background: transparent;
  border: none;
  text-align: left;
  border-radius: var(--radius-button);
  ${props => (props.$danger ? 'color: var(--destructive);' : '')}

  &:not(:first-child) {
    border-top: none;
  }

  /* Context-menu rows use the standard rounded-row style and muted hover (see
     docs/design-system.md), overriding ActionButton's green action hover and
     between-row borders. */
  &:hover {
    background-color: ${props =>
      props.$danger ? 'var(--destructive)' : 'var(--surface-elevated)'};
    color: ${props =>
      props.$danger ? 'var(--destructive-foreground)' : 'var(--foreground)'};
  }

  &:hover,
  &:focus-visible {
    outline: none;
  }
`;
